document.documentElement.classList.remove('no-js');

const SHOPIFY_ROOT = window.Shopify?.routes?.root || '/';
const CART_SECTION_ID = 'cart-drawer';
const PICKUP_SECTION_ID = 'pickup-availability';

const debounce = (callback, wait = 250) => {
  let timeoutId;

  return (...args) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => callback(...args), wait);
  };
};

const hasElements = (selector, scope = document) => Boolean(scope.querySelector(selector));

const createIdleDeadlineFallback = () => ({
  didTimeout: true,
  timeRemaining: () => 0
});

const scheduleIdleTask = (callback, { timeout = 1500 } = {}) => {
  if (typeof window.requestIdleCallback === 'function') {
    return window.requestIdleCallback(callback, { timeout });
  }

  return window.setTimeout(() => callback(createIdleDeadlineFallback()), 1);
};

const scheduleAfterLoad = (callback) => {
  if (document.readyState === 'complete') {
    callback();
    return;
  }

  window.addEventListener('load', callback, { once: true });
};

const scheduleDeferredTasks = (tasks, { timeout = 1500 } = {}) => {
  const pendingTasks = tasks.filter((task) => typeof task === 'function');
  if (!pendingTasks.length) return;

  const runNextBatch = (deadline = createIdleDeadlineFallback()) => {
    while (pendingTasks.length) {
      if (!deadline.didTimeout && deadline.timeRemaining() < 8) {
        break;
      }

      const task = pendingTasks.shift();
      task();
    }

    if (pendingTasks.length) {
      scheduleIdleTask(runNextBatch, { timeout });
    }
  };

  scheduleAfterLoad(() => {
    window.requestAnimationFrame(() => {
      scheduleIdleTask(runNextBatch, { timeout });
    });
  });
};

const formatMoney = (cents) => {
  if (window.Shopify?.formatMoney) {
    return window.Shopify.formatMoney(cents, window.theme?.moneyFormat);
  }

  return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'USD'
  }).format((cents || 0) / 100);
};

const setBodyScrollLock = () => {
  document.body.classList.toggle('overflow-hidden', Boolean(document.querySelector('[data-drawer].is-open')));
};

const FOCUSABLE_SELECTORS = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"]),details>summary';

const getFocusableElements = (container) =>
  [...container.querySelectorAll(FOCUSABLE_SELECTORS)].filter(
    (el) => !el.closest('[hidden]') && el.offsetParent !== null
  );

const trapFocus = (container, opener = null) => {
  container._focusTrapOpener = opener;

  const onKeydown = (e) => {
    if (e.key !== 'Tab') return;
    const focusable = getFocusableElements(container);
    if (focusable.length < 2) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  container._focusTrapHandler = onKeydown;
  container.addEventListener('keydown', onKeydown);
};

const releaseFocus = (container) => {
  if (container._focusTrapHandler) {
    container.removeEventListener('keydown', container._focusTrapHandler);
    delete container._focusTrapHandler;
  }
  const opener = container._focusTrapOpener;
  delete container._focusTrapOpener;
  if (opener?.isConnected) opener.focus({ preventScroll: true });
};

const openDrawer = (target) => {
  const drawer = typeof target === 'string' ? document.querySelector(target) : target;
  if (!drawer) return;

  const opener = document.activeElement;
  drawer.classList.add('is-open');
  drawer.setAttribute('aria-hidden', 'false');
  setBodyScrollLock();
  trapFocus(drawer, opener);
  window.requestAnimationFrame(() => {
    const first = getFocusableElements(drawer)[0];
    if (first) first.focus({ preventScroll: true });
  });
  window.LakiEvents?.emit(window.LakiEvents.EVENTS.DRAWER_OPEN, { drawer });
};

const closeDrawer = (target) => {
  const drawer = typeof target === 'string' ? document.querySelector(target) : target;
  if (!drawer) return;

  drawer.classList.remove('is-open');
  drawer.setAttribute('aria-hidden', 'true');
  setBodyScrollLock();
  releaseFocus(drawer);
  window.LakiEvents?.emit(window.LakiEvents.EVENTS.DRAWER_CLOSE, { drawer });
};

const parseHTML = (markup) => new DOMParser().parseFromString(markup, 'text/html');

const buildUrl = (path) => new URL(path, window.location.origin);

const closeDialog = (dialog) => {
  if (!dialog) return;

  if (typeof dialog.close === 'function') {
    dialog.close();
    return;
  }

  dialog.removeAttribute('open');
};

const openDialog = (dialog) => {
  if (!dialog) return;

  if (typeof dialog.showModal === 'function') {
    dialog.showModal();
    return;
  }

  dialog.setAttribute('open', 'open');
};

const announceToScreenReader = (message) => {
  const liveRegion = document.querySelector('[data-cart-live-region]');
  if (!liveRegion) return;

  liveRegion.textContent = '';
  window.setTimeout(() => {
    liveRegion.textContent = message;
  }, 20);
};

const updateCartCount = (count) => {
  document.querySelectorAll('[data-cart-count], .cart-count-bubble').forEach((badge) => {
    badge.textContent = count;
  });
};

const replaceSection = (sectionId, html) => {
  const wrapper = document.getElementById(`shopify-section-${sectionId}`)
                || document.getElementById(`shopify-section-group-${sectionId}`);
  if (!wrapper || !html) return null;

  wrapper.outerHTML = html;
  return document.getElementById(`shopify-section-${sectionId}`)
       || document.getElementById(`shopify-section-group-${sectionId}`);
};

const extractSectionElement = (markup, sectionId) => {
  const markupDocument = parseHTML(markup);

  return markupDocument.getElementById(`shopify-section-${sectionId}`) || markupDocument.querySelector('.shopify-section');
};

const renderCartResponse = (response, { open = false, announce } = {}) => {
  if (response?.sections?.[CART_SECTION_ID]) {
    replaceSection(CART_SECTION_ID, response.sections[CART_SECTION_ID]);
  }

  if (typeof response?.item_count === 'number') {
    updateCartCount(response.item_count);
  }

  if (announce) {
    announceToScreenReader(announce);
  }

  if (open) {
    document.querySelector('laki-cart')?.open();
  }

  window.LakiEvents?.emit(window.LakiEvents.EVENTS.CART_UPDATE, {
    count: response?.item_count,
    token: response?.token
  });

  // Re-check core-charge quantities against whatever just changed. Calling
  // this from inside renderCartResponse itself (rather than only on initial
  // page load) is what makes the sync immediate instead of waiting for the
  // next full page load — core-charge-sync.js's own correction path also
  // calls renderCartResponse, so this re-enters once more and then finds
  // nothing left to correct; it isn't a real loop.
  window.LakiCoreCharge?.sync();
};

// Expose for component-atc-button.js which can't share this module scope
if (window.theme) window.theme.renderCartResponse = renderCartResponse;

// Safer cart drawer refresh: updates innerHTML of the existing laki-cart element
// rather than doing an outerHTML swap, so the custom element and its listeners
// are never removed from the DOM.
const refreshCartDrawer = ({ open: shouldOpen = false } = {}) => {
  const current = document.querySelector('laki-cart');
  const willOpen = shouldOpen || current?.classList.contains('is-open');

  return Promise.all([
    fetch(`${SHOPIFY_ROOT}cart.js`).then((r) => r.json()),
    fetch(`${SHOPIFY_ROOT}?sections=${CART_SECTION_ID}`).then((r) => r.json())
  ])
    .then(([cartData, sectionsData]) => {
      const sectionHtml = sectionsData?.[CART_SECTION_ID];
      const live = document.querySelector('laki-cart');

      if (sectionHtml && live) {
        const fresh = parseHTML(sectionHtml).querySelector('laki-cart');
        if (fresh) live.innerHTML = fresh.innerHTML;
      }

      updateCartCount(cartData?.item_count ?? 0);

      if (willOpen) document.querySelector('laki-cart')?.open();

      window.LakiEvents?.emit(window.LakiEvents.EVENTS.CART_UPDATE, {
        count: cartData?.item_count
      });

      window.LakiCoreCharge?.sync();

      return cartData;
    })
    .catch(() => {});
};

if (window.theme) window.theme.refreshCartDrawer = refreshCartDrawer;
if (window.theme) {
  window.theme.trapFocus = trapFocus;
  window.theme.releaseFocus = releaseFocus;
}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.onChange = debounce(this.onChange.bind(this), 250);
    this.onOutsideClick = this.onOutsideClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.input = this.querySelector('input[type="search"]');
    this.resultsContainer = this.querySelector('[data-laki-search-results]');
    this.sectionId = this.dataset.sectionId || 'predictive-search';
    this.abortController = null;

    if (!this.input || !this.resultsContainer) return;

    this.input.addEventListener('input', this.onChange);
    this.input.addEventListener('focus', () => {
      if (this.resultsContainer.innerHTML.trim() !== '') {
        this.open();
      }
    });

    document.addEventListener('click', this.onOutsideClick);
    document.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onOutsideClick);
    document.removeEventListener('keydown', this.onKeydown);
  }

  onOutsideClick(event) {
    if (!this.contains(event.target)) {
      this.close();
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  onChange() {
    const term = this.input.value.trim();

    if (term.length < 2) {
      this.close();
      this.resultsContainer.innerHTML = '';
      return;
    }

    this.getSearchResults(term);
  }

  getSearchResults(term) {
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();
    const searchParams = new URLSearchParams({
      q: term,
      'resources[type]': 'product,collection,page,article,query',
      'resources[limit]': '8',
      'resources[options][unavailable_products]': 'hide',
      section_id: this.sectionId
    });

    fetch(`${SHOPIFY_ROOT}search/suggest?${searchParams.toString()}`, {
      signal: this.abortController.signal
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Predictive search request failed with ${response.status}`);
        }

        return response.text();
      })
      .then((markup) => {
        const section = parseHTML(markup).querySelector(`#shopify-section-${this.sectionId}`);
        if (!section) return;

        this.resultsContainer.innerHTML = section.innerHTML;
        this.open();
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.close();
        throw error;
      });
  }

  open() {
    this.resultsContainer.classList.add('is-open');
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.resultsContainer.classList.remove('is-open');
    this.input?.setAttribute('aria-expanded', 'false');
  }
}

class CartDrawer extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-cart-remove]');
      if (removeButton) {
        event.preventDefault();
        this.updateLine(Number(removeButton.dataset.line), 0);
      }
    });

    this.addEventListener('change', (event) => {
      const input = event.target.closest('[data-cart-line-input]');
      if (!input) return;

      const nextQuantity = Math.max(0, Number(input.value || 0));
      input.value = nextQuantity;
      this.updateLine(Number(input.dataset.line), nextQuantity);
    });
  }

  open() {
    openDrawer(this);
  }

  close() {
    closeDrawer(this);
  }

  updateLine(line, quantity) {
    this.classList.add('is-loading');
    const wasOpen = this.classList.contains('is-open');

    fetch(`${SHOPIFY_ROOT}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity })
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart change failed with ${response.status}`);
        return response.json();
      })
      .then(async (response) => {
        await refreshCartDrawer({ open: wasOpen });
        announceToScreenReader(
          `Cart updated. ${response.item_count} item${response.item_count === 1 ? '' : 's'} in cart.`
        );
      })
      .finally(() => {
        document.querySelector('laki-cart')?.classList.remove('is-loading');
      });
  }
}

const bindAjaxCartForms = () => {
  document.querySelectorAll('form[data-ajax-cart-form]').forEach((form) => {
    if (form.dataset.ajaxBound === 'true') return;
    form.dataset.ajaxBound = 'true';

    form.addEventListener('submit', (event) => {
      if (event.submitter && !event.submitter.matches('[data-add-to-cart]')) {
        return;
      }

      event.preventDefault();

      const submitButton = event.submitter || form.querySelector('[data-add-to-cart]');
      const originalLabel = submitButton?.textContent;

      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = 'Adding...';
      }

      const formData = new FormData(form);
      formData.append('sections', CART_SECTION_ID);
      formData.append('sections_url', `${window.location.pathname}${window.location.search}`);

      fetch(`${SHOPIFY_ROOT}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: formData
      })
        .then(async (response) => {
          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.description || data.message || 'Unable to add this item to the cart.');
          }

          return data;
        })
        .then((response) => {
          renderCartResponse(response, {
            open: true,
            announce: window.theme?.strings?.addedToCart || 'Item added to cart.'
          });
          window.LakiEvents?.emit(window.LakiEvents.EVENTS.CART_ADD, {
            count: response?.item_count,
            items: response?.items
          });
        })
        .catch((error) => {
          announceToScreenReader(error.message);
          window.alert(error.message);
        })
        .finally(() => {
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = originalLabel;
          }
        });
    });
  });
};

const switchGalleryMedia = (root, mediaId) => {
  const gallery = root.querySelector('[data-product-gallery]');
  if (!gallery) return;

  const id = String(mediaId);

  gallery.querySelectorAll('[data-gallery-media]').forEach((item) => {
    const isActive = item.dataset.mediaId === id;
    item.classList.toggle('is-active', isActive);
    if (!isActive) item.querySelector('video')?.pause();
  });

  const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
  let activeIndex = -1;

  thumbs.forEach((thumb, i) => {
    const isActive = thumb.dataset.mediaId === id;
    thumb.classList.toggle('is-active', isActive);
    thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');
    if (isActive) activeIndex = i;
  });

  gallery.querySelectorAll('.product-gallery__nav-dot').forEach((dot, i) => {
    dot.classList.toggle('is-active', i === activeIndex);
  });

  const activeMedia = gallery.querySelector(`[data-gallery-media][data-media-id="${id}"]`);
  const zoomBtn = gallery.querySelector('[data-gallery-zoom-trigger]');
  if (zoomBtn) zoomBtn.hidden = activeMedia?.dataset.mediaType !== 'image';

  const activeThumb = thumbs[activeIndex];
  activeThumb?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
};

const bindGallerySwipe = (gallery) => {
  const stage = gallery.querySelector('.product-gallery__stage');
  if (!stage || gallery.dataset.swipeBound) return;
  gallery.dataset.swipeBound = 'true';

  let touchStartX = 0;
  let touchStartY = 0;

  stage.addEventListener('touchstart', (e) => {
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
  }, { passive: true });

  stage.addEventListener('touchend', (e) => {
    const deltaX = e.changedTouches[0].clientX - touchStartX;
    const deltaY = e.changedTouches[0].clientY - touchStartY;
    if (Math.abs(deltaX) < 50 || Math.abs(deltaX) < Math.abs(deltaY)) return;

    const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
    const activeIndex = thumbs.findIndex((t) => t.classList.contains('is-active'));
    const nextIndex = deltaX < 0
      ? Math.min(activeIndex + 1, thumbs.length - 1)
      : Math.max(activeIndex - 1, 0);

    if (nextIndex !== activeIndex) {
      const root = gallery.closest('[data-product-root]');
      switchGalleryMedia(root, thumbs[nextIndex].dataset.mediaId);
    }
  }, { passive: true });
};

const bindGalleryZoom = (gallery) => {
  const dialog = gallery.querySelector('[data-gallery-zoom-dialog]');
  const zoomTrigger = gallery.querySelector('[data-gallery-zoom-trigger]');
  const zoomImg = gallery.querySelector('.product-gallery__zoom-image');
  const closeBtn = gallery.querySelector('[data-gallery-zoom-close]');
  if (!dialog || !zoomTrigger || !zoomImg) return;

  zoomTrigger.addEventListener('click', () => {
    const activeMedia = gallery.querySelector('[data-gallery-media].is-active');
    const img = activeMedia?.querySelector('img');
    if (!img) return;
    const hiResSrc = (img.currentSrc || img.src).replace(/width=\d+/, 'width=2400');
    zoomImg.src = hiResSrc;
    zoomImg.alt = img.alt;
    dialog.showModal();
  });

  closeBtn?.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
};

const bindPickupDialogs = (scope = document) => {
  scope.querySelectorAll('dialog[data-pickup-dialog]').forEach((dialog) => {
    if (dialog.dataset.dialogBound === 'true') return;
    dialog.dataset.dialogBound = 'true';

    dialog.addEventListener('click', (event) => {
      const dialogCard = dialog.querySelector('[data-pickup-dialog-card]');
      if (!dialogCard) return;

      const bounds = dialogCard.getBoundingClientRect();
      const isInsideCard = (
        event.clientX >= bounds.left &&
        event.clientX <= bounds.right &&
        event.clientY >= bounds.top &&
        event.clientY <= bounds.bottom
      );

      if (!isInsideCard) {
        closeDialog(dialog);
      }
    });
  });
};

const hydratePickupAvailability = (container) => {
  const productTitle = container.dataset.productTitle || '';
  const hasOnlyDefaultVariant = container.dataset.hasOnlyDefaultVariant === 'true';

  container.querySelectorAll('[data-pickup-product-title]').forEach((element) => {
    element.textContent = productTitle;
  });

  container.querySelectorAll('[data-pickup-variant-title]').forEach((element) => {
    const title = element.textContent.trim();

    if (hasOnlyDefaultVariant || title === '' || title === 'Default Title') {
      element.remove();
    }
  });

  bindPickupDialogs(container);
};

const renderPickupAvailability = (root) => {
  const container = root.querySelector('[data-store-availability-container]');
  if (!container) return;

  const variantId = root.querySelector('[data-variant-id-input]')?.value;
  if (!variantId) {
    container.innerHTML = '';
    container.hidden = true;
    return;
  }

  container.hidden = false;
  container.setAttribute('aria-busy', 'true');

  fetch(`${SHOPIFY_ROOT}variants/${variantId}/?section_id=${PICKUP_SECTION_ID}`)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Pickup availability request failed with ${response.status}`);
      }

      return response.text();
    })
    .then((markup) => {
      const section = extractSectionElement(markup, PICKUP_SECTION_ID);
      if (!section || !section.innerHTML.trim()) {
        container.innerHTML = '';
        container.hidden = true;
        return;
      }

      container.innerHTML = section.innerHTML;
      container.hidden = false;
      hydratePickupAvailability(container);
    })
    .catch(() => {
      container.innerHTML = '';
      container.hidden = true;
    })
    .finally(() => {
      container.removeAttribute('aria-busy');
    });
};

// ── Filter Parts: accordion sections, price slider, mobile drawer ──────────
// Handles every .facets-layout on the page (collection or search). All
// listeners are delegated from the stable .facets-layout wrapper rather than
// bound to elements inside #facets-panel, since that panel's innerHTML is
// fully replaced on every AJAX filter update — direct listeners on its
// children would be lost the moment the user changes any filter.
const bindFacets = () => {
  document.querySelectorAll('.facets-layout').forEach((layout) => {
    if (layout.dataset.facetsBound === 'true') return;
    layout.dataset.facetsBound = 'true';

    let abortCtrl = null;
    let debounceTimer = null;

    const getResults = () => document.getElementById('facets-results');
    const getPanel = () => document.getElementById('facets-panel');
    const getForm = () => layout.querySelector('[data-facets-form]');

    const setLoading = (on) => {
      const el = getResults();
      if (el) el.classList.toggle('is-loading', on);
    };

    const buildUrl = () => {
      const form = getForm();
      if (!form) return location.href;
      const params = new URLSearchParams();
      for (const [k, v] of new FormData(form).entries()) {
        if (v.trim() !== '') params.append(k, v);
      }
      return `${form.action}?${params.toString()}`;
    };

    const initPanelWidgets = () => {
      initAccordions();
      initSearchWithinList();
      initViewMore();
      initPriceSliders();
    };

    const fetchFilters = async (url) => {
      if (abortCtrl) abortCtrl.abort();
      abortCtrl = new AbortController();
      setLoading(true);
      try {
        const res = await fetch(url, { signal: abortCtrl.signal });
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

        const newPanel = doc.getElementById('facets-panel');
        const newResults = doc.getElementById('facets-results');
        if (newPanel) getPanel().innerHTML = newPanel.innerHTML;
        if (newResults) getResults().innerHTML = newResults.innerHTML;

        history.pushState(null, '', url);
        initPanelWidgets();
        document.dispatchEvent(new CustomEvent('laki:facets:updated'));
      } catch (e) {
        if (e.name !== 'AbortError') console.error('[laki facets]', e);
      } finally {
        setLoading(false);
      }
    };

    /* Hide the no-JS fallback submit button — changes auto-apply instead */
    const applyBtn = layout.querySelector('.facets__apply-btn');
    if (applyBtn) applyBtn.style.display = 'none';

    /* ── Accordion sections — independently toggleable, default open ── */
    const initAccordions = () => {
      layout.querySelectorAll('[data-facets-accordion-toggle]').forEach((toggle) => {
        if (toggle.dataset.accordionBound === 'true') return;
        toggle.dataset.accordionBound = 'true';
        toggle.addEventListener('click', () => {
          const content = toggle.nextElementSibling;
          const isOpen = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
          if (content) content.hidden = isOpen;
        });
      });
    };

    /* ── Search-within-list: hide non-matching value rows as the shopper types ── */
    const initSearchWithinList = () => {
      layout.querySelectorAll('[data-facets-search-input]').forEach((input) => {
        if (input.dataset.searchBound === 'true') return;
        input.dataset.searchBound = 'true';
        input.addEventListener('input', () => {
          const q = input.value.trim().toLowerCase();
          const target = input.closest('.facets__section-content')?.querySelector('[data-facets-search-target]');
          if (!target) return;
          target.querySelectorAll('label').forEach((row) => {
            const matches = !q || row.textContent.toLowerCase().indexOf(q) !== -1;
            row.hidden = !matches;
          });
        });
      });
    };

    /* ── View more / view less for long value lists ── */
    const initViewMore = () => {
      layout.querySelectorAll('[data-facets-view-more]').forEach((btn) => {
        if (btn.dataset.viewMoreBound === 'true') return;
        btn.dataset.viewMoreBound = 'true';
        btn.addEventListener('click', () => {
          const content = btn.closest('.facets__section-content');
          const extras = content ? content.querySelectorAll('[data-facets-extra-value]') : [];
          const expanded = btn.classList.toggle('is-expanded');
          extras.forEach((row) => {
            row.hidden = !expanded;
          });
          const label = btn.querySelector('[data-facets-view-more-label]');
          if (label) label.textContent = expanded ? 'View less' : 'View more';
        });
      });
    };

    /* ── Dual-handle price slider ── */
    const initPriceSliders = () => {
      layout.querySelectorAll('[data-facets-price]').forEach((wrap) => {
        if (wrap.dataset.priceBound === 'true') return;
        wrap.dataset.priceBound = 'true';

        const min = wrap.querySelector('[data-price-input-min]');
        const max = wrap.querySelector('[data-price-input-max]');
        const fill = wrap.querySelector('[data-price-fill]');
        const minLabel = wrap.querySelector('[data-price-min-label]');
        const maxLabel = wrap.querySelector('[data-price-max-label]');
        const rangeMax = Number(wrap.dataset.priceMax) || 1;
        if (!min || !max) return;

        const render = () => {
          let minVal = Number(min.value);
          let maxVal = Number(max.value);
          if (minVal > maxVal) {
            [minVal, maxVal] = [maxVal, minVal];
          }
          const minPct = (minVal / rangeMax) * 100;
          const maxPct = (maxVal / rangeMax) * 100;
          if (fill) {
            fill.style.left = `${minPct}%`;
            fill.style.right = `${100 - maxPct}%`;
          }
          if (minLabel) minLabel.textContent = formatMoney(minVal);
          if (maxLabel) maxLabel.textContent = maxVal >= rangeMax ? `${formatMoney(maxVal)}+` : formatMoney(maxVal);
        };

        const formatMoney = (value) => (window.Shopify && Shopify.formatMoney ? Shopify.formatMoney(value * 100) : `$${value}`);

        min.addEventListener('input', () => {
          if (Number(min.value) > Number(max.value)) min.value = max.value;
          render();
        });
        max.addEventListener('input', () => {
          if (Number(max.value) < Number(min.value)) max.value = min.value;
          render();
        });
        min.addEventListener('change', () => fetchFilters(buildUrl()));
        max.addEventListener('change', () => fetchFilters(buildUrl()));

        render();
      });
    };

    initPanelWidgets();

    /* ── Delegated events (survive AJAX panel swaps) ── */
    layout.addEventListener('submit', (e) => {
      if (!e.target.closest('[data-facets-form]')) return;
      e.preventDefault();
      fetchFilters(buildUrl());
    });

    layout.addEventListener('change', (e) => {
      if (!e.target.closest('[data-facets-form]')) return;
      if (e.target.closest('[data-facets-price]')) return; // price sliders fetch on their own change handler above
      if (e.target.hasAttribute('data-fitment-filter-toggle')) return; // client-only toggle, not a real Shopify filter
      if (e.target.type === 'checkbox' || e.target.tagName === 'SELECT') fetchFilters(buildUrl());
    });

    layout.addEventListener('click', (e) => {
      const clearAll = e.target.closest('[data-facets-clear-all]');
      if (clearAll) {
        e.preventDefault();
        fetchFilters(clearAll.href);
        return;
      }

      const closePanelBtn = e.target.closest('[data-facets-close-panel]');
      if (closePanelBtn) {
        const results = getResults();
        if (results && window.innerWidth < 990) results.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    /* Relocated sort control lives outside this snippet's <form> — its own
       change handler (bound in the topbar markup) calls this directly. */
    layout.fetchFiltersUrl = (url) => fetchFilters(url);

    /* Browser back/forward */
    window.addEventListener('popstate', () => fetchFilters(location.href));
  });
};

// ── Grid/list view toggle — shared by every product grid (collection pages,
// search results) via data-view-toggle-scope / data-view-toggle /
// data-view-target attributes, so one implementation covers all of them
// instead of a copy per page. Each scope gets its own localStorage key so a
// shopper's view preference persists across visits.
const VIEW_TOGGLE_STORAGE_KEYS = {
  'collection-products': 'laki-view-products',
  'search-products': 'laki-view-products'
};

const bindViewToggles = () => {
  const reapplyAll = () => {
    document.querySelectorAll('[data-view-toggle-scope]').forEach((group) => {
      const scope = group.dataset.viewToggleScope;
      const storageKey = VIEW_TOGGLE_STORAGE_KEYS[scope] || `laki-view-${scope}`;
      let saved = 'grid';
      try {
        saved = localStorage.getItem(storageKey) || 'grid';
      } catch (_) {
        /* no-op */
      }
      document.querySelectorAll(`[data-view-target="${scope}"]`).forEach((target) => {
        target.setAttribute('data-view', saved);
      });
      group.querySelectorAll('[data-view-toggle]').forEach((btn) => {
        const active = btn.dataset.viewToggle === saved;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    });
  };

  document.querySelectorAll('[data-view-toggle-scope]').forEach((group) => {
    if (group.dataset.viewToggleBound === 'true') return;
    group.dataset.viewToggleBound = 'true';

    const scope = group.dataset.viewToggleScope;
    const storageKey = VIEW_TOGGLE_STORAGE_KEYS[scope] || `laki-view-${scope}`;

    group.querySelectorAll('[data-view-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        try {
          localStorage.setItem(storageKey, btn.dataset.viewToggle);
        } catch (_) {
          /* no-op */
        }
        reapplyAll();
      });
    });
  });

  reapplyAll();
  // Re-sync after the AJAX facet swap replaces #facets-results — the fresh
  // markup always comes back as the server default ("grid") otherwise.
  document.addEventListener('laki:facets:updated', reapplyAll);
};

const bindPartsFinder = () => {
  const esc = (s) => String(s == null ? '' : s).replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  document.querySelectorAll('[data-parts-finder]').forEach((section) => {
    if (section.dataset.partsFinderBound === 'true') return;
    section.dataset.partsFinderBound = 'true';

    const dataScript = section.querySelector('[data-pf-menu-data]');
    let brands = [];
    try {
      brands = JSON.parse(dataScript?.textContent || '[]');
    } catch (_) {
      brands = [];
    }

    const levels = ['brand', 'series', 'model'];
    const dropdowns = {};
    levels.forEach((level) => {
      dropdowns[level] = section.querySelector(`[data-pf-dropdown][data-pf-level="${level}"]`);
    });

    const findBtn = section.querySelector('[data-pf-find]');
    const placeholders = {};
    levels.forEach((level) => {
      const valueEl = dropdowns[level]?.querySelector('[data-pf-value]');
      placeholders[level] = valueEl ? valueEl.textContent : '';
    });

    const state = { brand: null, series: null, model: null };
    let optionsCache = { brand: [], series: [], model: [] };
    optionsCache.brand = brands;

    const getDropdownParts = (level) => {
      const root = dropdowns[level];
      if (!root) return {};
      return {
        root,
        trigger: root.querySelector('[data-pf-trigger]'),
        valueEl: root.querySelector('[data-pf-value]'),
        clearEl: root.querySelector('[data-pf-clear]'),
        panel: root.querySelector('[data-pf-panel]'),
        searchInput: root.querySelector('[data-pf-search]'),
        list: root.querySelector('[data-pf-list]')
      };
    };

    const closeAllPanels = () => {
      levels.forEach((level) => {
        const { root, panel, trigger } = getDropdownParts(level);
        if (!root || !panel) return;
        panel.hidden = true;
        root.classList.remove('is-open');
        trigger?.setAttribute('aria-expanded', 'false');
      });
    };

    const renderList = (level) => {
      const { list, searchInput } = getDropdownParts(level);
      if (!list) return;
      if (searchInput) searchInput.value = '';

      const items = optionsCache[level] || [];
      if (!items.length) {
        list.innerHTML = '<li class="pf-no-results">No options available</li>';
        return;
      }

      list.innerHTML = items
        .map((item, index) => `<li data-pf-option data-index="${index}">${esc(item.label)}</li>`)
        .join('');
    };

    const setDropdownEnabled = (level, enabled) => {
      const { root, trigger } = getDropdownParts(level);
      if (!root || !trigger) return;
      root.classList.toggle('is-disabled', !enabled);
      trigger.disabled = !enabled;
    };

    const clearLevel = (level, { cascade = true } = {}) => {
      const { root, valueEl, clearEl } = getDropdownParts(level);
      state[level] = null;
      if (root) root.classList.remove('has-value');
      if (valueEl) valueEl.textContent = placeholders[level];
      if (clearEl) clearEl.hidden = true;

      if (cascade) {
        const deeperIndex = levels.indexOf(level) + 1;
        for (let i = deeperIndex; i < levels.length; i += 1) {
          clearLevel(levels[i], { cascade: false });
          optionsCache[levels[i]] = [];
          setDropdownEnabled(levels[i], false);
        }
      }
    };

    const updateFindButtonState = () => {
      if (!findBtn) return;
      findBtn.disabled = !state.brand;
    };

    const selectOption = (level, index) => {
      const item = (optionsCache[level] || [])[index];
      if (!item) return;

      const { root, valueEl, clearEl } = getDropdownParts(level);
      state[level] = item;
      if (root) root.classList.add('has-value');
      if (valueEl) valueEl.textContent = item.label;
      if (clearEl) clearEl.hidden = false;

      closeAllPanels();

      if (level === 'brand') {
        clearLevel('series', { cascade: false });
        clearLevel('model', { cascade: false });
        optionsCache.series = item.series || [];
        optionsCache.model = [];
        setDropdownEnabled('series', optionsCache.series.length > 0);
        setDropdownEnabled('model', false);
      } else if (level === 'series') {
        clearLevel('model', { cascade: false });
        optionsCache.model = item.models || [];
        setDropdownEnabled('model', optionsCache.model.length > 0);
      }

      updateFindButtonState();
    };

    const openPanel = (level) => {
      const { root, panel, trigger, searchInput } = getDropdownParts(level);
      if (!root || !panel || root.classList.contains('is-disabled')) return;
      const wasOpen = !panel.hidden;
      closeAllPanels();
      if (wasOpen) return;

      renderList(level);
      panel.hidden = false;
      root.classList.add('is-open');
      trigger?.setAttribute('aria-expanded', 'true');
      window.setTimeout(() => searchInput?.focus(), 0);
    };

    levels.forEach((level) => {
      const { trigger, searchInput, list } = getDropdownParts(level);

      trigger?.addEventListener('click', () => openPanel(level));

      searchInput?.addEventListener('input', () => {
        const query = searchInput.value.trim().toLowerCase();
        const items = list?.querySelectorAll('[data-pf-option]') || [];
        let visibleCount = 0;
        items.forEach((li) => {
          const match = li.textContent.toLowerCase().includes(query);
          li.hidden = !match;
          if (match) visibleCount += 1;
        });
        let noResultsEl = list?.querySelector('.pf-no-results');
        if (visibleCount === 0 && (optionsCache[level] || []).length > 0) {
          if (!noResultsEl) {
            noResultsEl = document.createElement('li');
            noResultsEl.className = 'pf-no-results';
            noResultsEl.textContent = 'No matches found';
            list?.appendChild(noResultsEl);
          }
          noResultsEl.hidden = false;
        } else if (noResultsEl) {
          noResultsEl.hidden = true;
        }
      });

      list?.addEventListener('click', (e) => {
        const option = e.target.closest('[data-pf-option]');
        if (!option) return;
        selectOption(level, Number(option.dataset.index));
      });
    });

    section.addEventListener('click', (e) => {
      const clearBtn = e.target.closest('[data-pf-clear]');
      if (clearBtn) {
        e.stopPropagation();
        const level = clearBtn.closest('[data-pf-dropdown]')?.dataset.pfLevel;
        if (level) {
          clearLevel(level);
          updateFindButtonState();
        }
        return;
      }

      if (e.target.closest('[data-pf-clear-all]')) {
        levels.forEach((level) => clearLevel(level, { cascade: false }));
        optionsCache = { brand: brands, series: [], model: [] };
        setDropdownEnabled('series', false);
        setDropdownEnabled('model', false);
        updateFindButtonState();
        return;
      }

      if (e.target.closest('[data-pf-find]')) {
        const destination = (state.model || state.series || state.brand)?.url;
        if (destination) window.location.href = destination;
      }
    });

    document.addEventListener('click', (e) => {
      const insideAnyDropdown = levels.some((level) => dropdowns[level]?.contains(e.target));
      if (!insideAnyDropdown) closeAllPanels();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeAllPanels();
    });

    setDropdownEnabled('series', false);
    setDropdownEnabled('model', false);
    updateFindButtonState();
  });
};

const bindRelatedProducts = () => {
  document.querySelectorAll('[data-related-products]').forEach((section) => {
    if (section.dataset.relatedBound === 'true') return;
    section.dataset.relatedBound = 'true';

    // The theme-editor-only "Related products will appear here" placeholder
    // is a separate <section> with no mechanism of its own to disappear once
    // real (or fallback) content has actually loaded — hide it here instead,
    // once the fetch has settled one way or another.
    const hidePreview = () => {
      const preview = document.getElementById(`${section.id}-preview`);
      if (preview) preview.hidden = true;
    };

    const url = section.dataset.url;
    const target = section.querySelector('[data-related-products-inner]');
    if (!url || !target) {
      section.hidden = true;
      hidePreview();
      return;
    }

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Related products request failed with ${response.status}`);
        return response.text();
      })
      .then((markup) => {
        const responseInner = parseHTML(markup).querySelector('[data-related-products-inner]');
        const hasProducts = responseInner?.querySelector('.pt-related-card');

        if (!responseInner || !hasProducts) {
          const fallback = section.querySelector('[data-related-fallback]');
          if (fallback && fallback.querySelector('.pt-related-card')) {
            fallback.hidden = false;
            section.hidden = false;
          } else {
            section.hidden = true;
          }
          hidePreview();
          return;
        }

        target.innerHTML = responseInner.innerHTML;
        section.hidden = false;
        hidePreview();
        bindAjaxCartForms();
      })
      .catch(() => {
        section.hidden = true;
        hidePreview();
      });
  });
};

// Binds checkbox recalculation, variant sync, and Add All to Cart for every
// .fbt section currently in the DOM that has its data island present. Safe to
// call repeatedly — already-bound sections are skipped, and sections without
// content yet (the auto-fetch container before it loads) are skipped too,
// so this can run again later once that fetch populates them.
const bindFrequentlyBoughtTogether = () => {
  document.querySelectorAll('.fbt').forEach((sec) => {
    if (sec.dataset.fbtBound === 'true') return;
    const dataScript = sec.querySelector('script[data-fbt-data]');
    if (!dataScript) return;
    sec.dataset.fbtBound = 'true';

    let cfg;
    try {
      cfg = JSON.parse(dataScript.textContent);
    } catch (_) {
      return;
    }

    const fmt = (cents) => {
      if (window.Shopify && Shopify.formatMoney) return Shopify.formatMoney(cents, cfg.moneyFormat);
      return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: cfg.currency });
    };

    let basePrice = cfg.basePrice;
    let baseFullPrice = cfg.baseFullPrice;
    let baseVarId = cfg.baseVariantId;

    const btn = sec.querySelector('[data-fbt-add]');
    const totalEl = sec.querySelector('[data-fbt-total]');
    const savingsEl = sec.querySelector('[data-fbt-savings]');
    const checks = Array.from(sec.querySelectorAll('[data-fbt-check]'));

    const recalc = () => {
      let total = basePrice;
      let full = baseFullPrice;
      checks.forEach((c) => {
        if (c.checked) {
          total += Number(c.dataset.price || 0);
          full += Number(c.dataset.fullPrice || c.dataset.price || 0);
        }
      });
      if (totalEl) totalEl.textContent = fmt(total);
      if (savingsEl) {
        const savings = full - total;
        if (savings > 0) {
          const pct = Math.round((savings / full) * 100);
          savingsEl.textContent = `You save ${fmt(savings)} (${pct}%)`;
          savingsEl.hidden = false;
        } else {
          savingsEl.hidden = true;
        }
      }
    };

    // Keep FBT in sync when the shopper switches variant on the main product form
    const syncVariant = () => {
      const selected = Array.from(
        document.querySelectorAll('.pt-option input[type="radio"]:checked')
      ).map((r) => r.value);

      const match = (cfg.variants || []).find((v) => {
        const opts = [v.option1, v.option2, v.option3].filter(Boolean);
        return opts.length === selected.length && opts.every((o, i) => o === selected[i]);
      });

      if (match) {
        basePrice = match.price;
        baseFullPrice = match.compare_at_price && match.compare_at_price > match.price ? match.compare_at_price : match.price;
        baseVarId = match.id;
        if (btn) btn.dataset.baseVariant = match.id;
        recalc();
      }
    };

    document.querySelectorAll('.pt-option input[type="radio"]').forEach((r) => {
      r.addEventListener('change', syncVariant);
    });

    checks.forEach((c) => c.addEventListener('change', recalc));

    btn?.addEventListener('click', async () => {
      const items = [{ id: Number(baseVarId), quantity: 1 }];
      checks.forEach((c) => {
        if (c.checked) items.push({ id: Number(c.dataset.variantId), quantity: 1 });
      });
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'Adding…';
      }
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!res.ok) throw new Error(res.status);
        if (btn) btn.textContent = '✓ Added to Cart';
        setTimeout(() => {
          if (btn) {
            btn.disabled = false;
            btn.textContent = cfg.buttonLabel;
          }
        }, 2500);
      } catch (_) {
        if (btn) {
          btn.disabled = false;
          btn.textContent = cfg.buttonLabel;
        }
      }
    });

    recalc();
  });
};

// Fetches Shopify's "complementary" recommendations for sections that have no
// manual override blocks configured — mirrors bindRelatedProducts' pattern,
// including hiding the theme-editor-only preview placeholder once settled.
const bindFrequentlyBoughtTogetherAuto = () => {
  document.querySelectorAll('[data-fbt-auto]').forEach((section) => {
    if (section.dataset.fbtAutoBound === 'true') return;
    section.dataset.fbtAutoBound = 'true';

    const hidePreview = () => {
      const preview = document.getElementById(`${section.id}-preview`);
      if (preview) preview.hidden = true;
    };

    const url = section.dataset.url;
    const target = section.querySelector('[data-fbt-auto-inner]');
    if (!url || !target) {
      section.hidden = true;
      hidePreview();
      return;
    }

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Frequently bought together request failed with ${response.status}`);
        return response.text();
      })
      .then((markup) => {
        const responseInner = parseHTML(markup).querySelector('[data-fbt-auto-inner]');
        const hasProducts = responseInner?.querySelector('.fbt__item');

        if (!responseInner || !hasProducts) {
          section.hidden = true;
          hidePreview();
          return;
        }

        target.innerHTML = responseInner.innerHTML;
        section.hidden = false;
        hidePreview();
        bindFrequentlyBoughtTogether();
      })
      .catch(() => {
        section.hidden = true;
        hidePreview();
      });
  });
};

const bindComplementaryProducts = () => {
  document.querySelectorAll('[data-complementary-products]').forEach((container) => {
    if (container.dataset.complementaryBound === 'true') return;
    container.dataset.complementaryBound = 'true';

    const url = container.dataset.url;
    const target = container.querySelector('[data-complementary-products-inner]');
    if (!url || !target) {
      container.hidden = true;
      return;
    }

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Complementary products request failed with ${response.status}`);
        }

        return response.text();
      })
      .then((markup) => {
        const responseInner = parseHTML(markup).querySelector('[data-complementary-products-inner]');
        const hasProducts = responseInner?.querySelector('.product-card');

        if (!responseInner || !hasProducts) {
          container.hidden = true;
          return;
        }

        target.innerHTML = responseInner.innerHTML;
        container.hidden = false;
        bindAjaxCartForms();
      })
      .catch(() => {
        container.hidden = true;
      })
      .finally(() => {
        container.removeAttribute('aria-busy');
      });
  });
};

const submitFacetForm = (form) => {
  if (typeof form.requestSubmit === 'function') {
    form.requestSubmit();
    return;
  }

  form.submit();
};

const bindFacetForms = () => {
  document.querySelectorAll('[data-facets-form]').forEach((form) => {
    if (form.dataset.facetsBound === 'true') return;
    form.dataset.facetsBound = 'true';

    const debouncedSubmit = debounce(() => submitFacetForm(form), 450);

    form.addEventListener('change', (event) => {
      const field = event.target;
      if (!field.matches('select, input[type="checkbox"], input[type="radio"]')) return;

      submitFacetForm(form);
    });

    form.querySelectorAll('input[type="number"]').forEach((input) => {
      input.addEventListener('input', () => {
        debouncedSubmit();
      });

      input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;

        event.preventDefault();
        submitFacetForm(form);
      });
    });
  });
};

const bindFaqSections = () => {
  document.querySelectorAll('[data-faq-root]').forEach((root) => {
    if (root.dataset.faqBound === 'true') return;
    root.dataset.faqBound = 'true';

    const items = [...root.querySelectorAll('[data-faq-item]')];
    const filters = [...root.querySelectorAll('[data-faq-filter]')];
    const searchInput = root.querySelector('[data-faq-search-input]');
    const emptyState = root.querySelector('[data-faq-empty]');
    let activeCategory = 'all';

    const updateFilterState = () => {
      filters.forEach((filter) => {
        filter.classList.toggle('is-active', filter.dataset.faqFilter === activeCategory);
      });
    };

    const applyFilters = () => {
      const term = (searchInput?.value || '').trim().toLowerCase();
      let visibleCount = 0;

      items.forEach((item) => {
        const matchesCategory = activeCategory === 'all' || item.dataset.category === activeCategory;
        const matchesSearch = term === '' || (item.dataset.search || '').includes(term);
        const isVisible = matchesCategory && matchesSearch;

        item.hidden = !isVisible;
        if (isVisible) {
          visibleCount += 1;
        }
      });

      if (emptyState) {
        emptyState.hidden = visibleCount !== 0;
      }
    };

    filters.forEach((filter) => {
      filter.addEventListener('click', () => {
        activeCategory = filter.dataset.faqFilter || 'all';
        updateFilterState();
        applyFilters();
      });
    });

    if (searchInput) {
      searchInput.addEventListener('input', debounce(applyFilters, 150));
    }

    updateFilterState();
    applyFilters();
  });
};

const updateStageCarouselState = (carousel) => {
  const track = carousel.querySelector('[data-stage-track]');
  const prevButton = carousel.querySelector('[data-stage-prev]');
  const nextButton = carousel.querySelector('[data-stage-next]');
  if (!track || !prevButton || !nextButton) return;

  const carouselItems = [...track.querySelectorAll('[data-stage-slide], .home-superstage__single-slide')];

  const syncStageBundleFooter = () => {
    const slides = [...track.querySelectorAll('[data-stage-slide]')];
    const addButton = carousel.closest('.home-superstage__card')?.querySelector('[data-stage-bundle-add]');
    if (!slides.length || !addButton) return;

    const activeSlide = slides.reduce((closest, slide) => {
      if (!closest) return slide;

      const closestDistance = Math.abs(closest.offsetLeft - track.scrollLeft);
      const slideDistance = Math.abs(slide.offsetLeft - track.scrollLeft);
      return slideDistance < closestDistance ? slide : closest;
    }, null);

    if (!activeSlide) return;

    const total = carousel.closest('.home-superstage__card')?.querySelector('[data-stage-bundle-total]');
    const compare = carousel.closest('.home-superstage__card')?.querySelector('[data-stage-bundle-compare]');
    const kicker = carousel.closest('.home-superstage__card')?.querySelector('[data-stage-bundle-kicker]');
    const canAdd = activeSlide.dataset.stageSlideCanAdd === 'true';
    const items = activeSlide.dataset.stageSlideItems || '[]';
    const compareValue = activeSlide.dataset.stageSlideCompare || '';
    const savingsValue = activeSlide.dataset.stageSlideSavings || '';

    addButton.dataset.stageItems = items;
    addButton.disabled = !canAdd || items === '[]';
    addButton.querySelector('span').textContent = canAdd
      ? addButton.dataset.stagePrimaryLabel || 'Add to cart'
      : addButton.dataset.stageUnavailableLabel || 'Unavailable';

    if (total) {
      total.textContent = activeSlide.dataset.stageSlideTotal || '$0.00';
    }

    if (compare) {
      compare.textContent = compareValue;
      compare.hidden = compareValue.length === 0;
    }

    if (kicker) {
      kicker.textContent = savingsValue;
      kicker.hidden = savingsValue.length === 0;
    }
  };

  const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
  const canScroll = carouselItems.length > 1;
  const activeIndex = canScroll ? getStageCardActiveIndex(track, carouselItems) : 0;

  prevButton.hidden = !canScroll;
  nextButton.hidden = !canScroll;

  if (!canScroll) {
    prevButton.disabled = true;
    nextButton.disabled = true;
    syncStageBundleFooter();
    return;
  }

  prevButton.disabled = activeIndex <= 0;
  nextButton.disabled = activeIndex >= carouselItems.length - 1;
  syncStageBundleFooter();
};

const getStageCardActiveIndex = (track, slides) => {
  if (!track || !slides.length) return 0;

  return slides.reduce((closestIndex, slide, index) => {
    if (closestIndex === -1) return index;

    const closestDistance = Math.abs(slides[closestIndex].offsetLeft - track.scrollLeft);
    const slideDistance = Math.abs(slide.offsetLeft - track.scrollLeft);
    return slideDistance < closestDistance ? index : closestIndex;
  }, -1);
};

const updateStageCardCarouselState = (root) => {
  const track = root.querySelector('[data-stage-card-track]');
  const controls = root.querySelector('[data-stage-card-controls]');
  const prevButton = root.querySelector('[data-stage-card-prev]');
  const nextButton = root.querySelector('[data-stage-card-next]');
  const dots = [...root.querySelectorAll('[data-stage-card-dot]')];
  const slides = [...root.querySelectorAll('[data-stage-card-slide]')];
  if (!track || !controls || !prevButton || !nextButton || !slides.length) return;

  const isCompact = window.matchMedia('(max-width: 1280px)').matches;
  if (!isCompact && track.scrollLeft !== 0) {
    track.scrollLeft = 0;
  }

  const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
  const canScroll = isCompact && slides.length > 1 && maxScroll > 12;
  const activeIndex = canScroll ? getStageCardActiveIndex(track, slides) : 0;

  controls.hidden = !canScroll;
  prevButton.disabled = !canScroll || activeIndex <= 0;
  nextButton.disabled = !canScroll || activeIndex >= slides.length - 1;

  dots.forEach((dot, index) => {
    const isActive = index === activeIndex;
    dot.classList.toggle('is-active', isActive);
    dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    dot.tabIndex = isActive ? 0 : -1;
  });
};

const bindHomeSuperstage = () => {
  document.querySelectorAll('[data-stage-department-root]').forEach((root) => {
    if (root.dataset.stageDepartmentBound === 'true') return;
    root.dataset.stageDepartmentBound = 'true';

    const overlay = root.querySelector('[data-stage-department-overlay]');
    const triggers = [...root.querySelectorAll('[data-stage-department-trigger]')];
    const closeButtons = root.querySelectorAll('[data-stage-department-close]');

    const syncTriggerState = (activePanelId) => {
      triggers.forEach((trigger) => {
        const isActive = trigger.dataset.departmentTab === activePanelId && root.classList.contains('is-mega-open');
        trigger.classList.toggle('is-active', isActive);
        trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    };

    const openOverlay = (panelId) => {
      if (!overlay) return;
      setActiveDepartmentPanel(root, panelId);
      root.classList.add('is-mega-open');
      overlay.hidden = false;
      syncTriggerState(panelId);
    };

    const closeOverlay = () => {
      if (!overlay) return;
      root.classList.remove('is-mega-open');
      overlay.hidden = true;
      triggers.forEach((trigger) => {
        trigger.classList.remove('is-active');
        trigger.setAttribute('aria-selected', 'false');
      });
    };

    if (triggers.length) {
      setActiveDepartmentPanel(root, triggers[0].dataset.departmentTab);
    }

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const panelId = trigger.dataset.departmentTab;
        const isSamePanel = root.classList.contains('is-mega-open') && trigger.classList.contains('is-active');

        if (isSamePanel) {
          closeOverlay();
          return;
        }

        openOverlay(panelId);
      });
    });

    closeButtons.forEach((button) => {
      button.addEventListener('click', closeOverlay);
    });

    overlay?.addEventListener('click', (event) => {
      if (event.target === overlay) {
        closeOverlay();
      }
    });

    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeOverlay();
      }
    });
  });

  document.querySelectorAll('[data-stage-carousel]').forEach((carousel) => {
    if (carousel.dataset.stageCarouselBound === 'true') return;
    carousel.dataset.stageCarouselBound = 'true';

    const track = carousel.querySelector('[data-stage-track]');
    const prevButton = carousel.querySelector('[data-stage-prev]');
    const nextButton = carousel.querySelector('[data-stage-next]');
    if (!track || !prevButton || !nextButton) return;

    const items = [...track.querySelectorAll('[data-stage-slide], .home-superstage__single-slide')];

    const scrollTrack = (direction) => {
      if (!items.length) return;

      const activeIndex = getStageCardActiveIndex(track, items);
      const nextItem = items[Math.max(0, Math.min(activeIndex + direction, items.length - 1))];
      if (!nextItem) return;

      nextItem.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    };

    prevButton.addEventListener('click', () => scrollTrack(-1));
    nextButton.addEventListener('click', () => scrollTrack(1));
    track.addEventListener('scroll', debounce(() => updateStageCarouselState(carousel), 40));
    window.addEventListener('resize', debounce(() => updateStageCarouselState(carousel), 80));
    updateStageCarouselState(carousel);
  });

  document.querySelectorAll('[data-stage-card-carousel]').forEach((root) => {
    if (root.dataset.stageCardCarouselBound === 'true') return;
    root.dataset.stageCardCarouselBound = 'true';

    const track = root.querySelector('[data-stage-card-track]');
    const prevButton = root.querySelector('[data-stage-card-prev]');
    const nextButton = root.querySelector('[data-stage-card-next]');
    const slides = [...root.querySelectorAll('[data-stage-card-slide]')];
    const dots = [...root.querySelectorAll('[data-stage-card-dot]')];
    if (!track || !prevButton || !nextButton || !slides.length) return;

    const scrollToSlide = (index) => {
      const nextSlide = slides[Math.max(0, Math.min(index, slides.length - 1))];
      if (!nextSlide) return;

      nextSlide.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    };

    prevButton.addEventListener('click', () => {
      const activeIndex = getStageCardActiveIndex(track, slides);
      scrollToSlide(activeIndex - 1);
    });

    nextButton.addEventListener('click', () => {
      const activeIndex = getStageCardActiveIndex(track, slides);
      scrollToSlide(activeIndex + 1);
    });

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => scrollToSlide(index));
    });

    track.addEventListener('scroll', debounce(() => updateStageCardCarouselState(root), 40));
    window.addEventListener('resize', debounce(() => updateStageCardCarouselState(root), 80));
    updateStageCardCarouselState(root);
  });

  document.querySelectorAll('[data-stage-bundle-add]').forEach((button) => {
    if (button.dataset.stageBundleBound === 'true') return;
    button.dataset.stageBundleBound = 'true';

    button.addEventListener('click', () => {
      const items = JSON.parse(button.dataset.stageItems || '[]');
      if (!items.length) return;

      const originalLabel = button.textContent.trim();
      button.disabled = true;
      button.textContent = 'Adding...';

      fetch(`${SHOPIFY_ROOT}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items,
          sections: CART_SECTION_ID,
          sections_url: `${window.location.pathname}${window.location.search}`
        })
      })
        .then(async (response) => {
          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.description || data.message || 'Unable to add this bundle to the cart.');
          }

          return data;
        })
        .then((response) => {
          renderCartResponse(response, {
            open: true,
            announce: button.dataset.stageSuccessMessage || 'Bundle added to cart.'
          });
        })
        .catch((error) => {
          announceToScreenReader(error.message);
          window.alert(error.message);
        })
        .finally(() => {
          button.disabled = false;
          button.textContent = originalLabel;
        });
    });
  });
};

const collectSelectedOptionValueIds = (root) => {
  const ids = [];
  root.querySelectorAll('[data-option-input]:checked').forEach((input) => {
    if (input.dataset.optionValueId) ids.push(input.dataset.optionValueId);
  });
  root.querySelectorAll('[data-option-select]').forEach((select) => {
    const opt = select.selectedOptions[0];
    if (opt?.dataset.optionValueId) ids.push(opt.dataset.optionValueId);
  });
  return ids;
};

const buildProductHistoryUrl = (productUrl, selectedOptionValueIds) => {
  const url = buildUrl(productUrl);

  url.searchParams.delete('section_id');
  url.searchParams.delete('variant');

  if (selectedOptionValueIds.length > 0) {
    url.searchParams.set('option_values', selectedOptionValueIds.join(','));
  } else {
    url.searchParams.delete('option_values');
  }

  return url;
};

const requestProductSectionUpdate = (root, changedInput) => {
  const sectionId = root.dataset.sectionId;
  const sectionWrapper = root.closest(`#shopify-section-${sectionId}`) || root.closest('.shopify-section');
  const productUrl = changedInput.dataset.productUrl || root.dataset.productUrl || window.location.pathname;
  const selectedOptionValueIds = collectSelectedOptionValueIds(root);
  const quantity = root.querySelector('input[name="quantity"]')?.value || '1';
  const requestUrl = buildProductHistoryUrl(productUrl, selectedOptionValueIds);
  const fallbackUrl = requestUrl.toString();

  requestUrl.searchParams.set('section_id', sectionId);

  if (root.variantRequestController) {
    root.variantRequestController.abort();
  }

  root.variantRequestController = new AbortController();
  root.classList.add('is-loading');

  fetch(requestUrl.toString(), {
    signal: root.variantRequestController.signal
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Product section request failed with ${response.status}`);
      }

      return response.text();
    })
    .then((markup) => {
      const nextSection = extractSectionElement(markup, sectionId);
      if (!nextSection || !sectionWrapper) {
        throw new Error('Updated product section could not be loaded.');
      }

      sectionWrapper.outerHTML = nextSection.outerHTML;

      const nextRoot = document.querySelector(`#shopify-section-${sectionId} [data-product-root]`);
      const quantityInput = nextRoot?.querySelector('input[name="quantity"]');

      if (quantityInput) {
        quantityInput.value = quantity;
      }

      bindAjaxCartForms();
      bindProductEnhancements();
      bindComplementaryProducts();

      const historyUrl = nextRoot?.dataset.historyUrl || fallbackUrl;
      window.history.replaceState({}, '', historyUrl);
      document.getElementById(changedInput.id)?.focus();

      window.LakiEvents?.emit(window.LakiEvents.EVENTS.VARIANT_CHANGE, {
        sectionId,
        root: nextRoot
      });
    })
    .catch((error) => {
      if (error.name === 'AbortError') return;
      window.location.assign(fallbackUrl);
    })
    .finally(() => {
      root.classList.remove('is-loading');
    });
};

const queuePickupAvailabilityRender = (root, { deferred = false } = {}) => {
  if (!root || root.dataset.pickupAvailabilityBooted === 'true') return;
  root.dataset.pickupAvailabilityBooted = 'true';

  const render = () => {
    if (!root.isConnected) return;
    renderPickupAvailability(root);
  };

  if (deferred) {
    scheduleAfterLoad(() => {
      scheduleIdleTask(() => {
        window.requestAnimationFrame(render);
      }, { timeout: 1800 });
    });
    return;
  }

  render();
};

const bindMobileAtcBar = (root) => {
  const bar = root.querySelector('[data-mobile-atc]');
  if (!bar) return;

  if (!window.matchMedia('(max-width: 749px)').matches) return;

  bar.removeAttribute('hidden');

  if (!('IntersectionObserver' in window)) return;

  const mainAtcBtn = root.querySelector('[data-add-to-cart]');
  if (!mainAtcBtn) return;

  const observer = new IntersectionObserver(([entry]) => {
    const visible = !entry.isIntersecting;
    bar.classList.toggle('is-visible', visible);
    bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }, { threshold: 0 });

  observer.observe(mainAtcBtn);

  bar.querySelector('[data-mobile-atc-btn]')?.addEventListener('click', () => {
    root.querySelector('[data-add-to-cart]')?.click();
  });
};

const bindProductEnhancements = ({ deferPickupAvailability = false } = {}) => {
  document.querySelectorAll('[data-product-root]').forEach((root) => {
    if (root.dataset.productBound === 'true') return;
    root.dataset.productBound = 'true';

    root.querySelectorAll('[data-option-input]').forEach((input) => {
      input.addEventListener('change', () => requestProductSectionUpdate(root, input));
    });

    root.querySelectorAll('[data-option-select]').forEach((select) => {
      select.addEventListener('change', () => {
        const selectedOpt = select.selectedOptions[0];
        requestProductSectionUpdate(root, {
          dataset: {
            productUrl: selectedOpt?.dataset.productUrl || root.dataset.productUrl || ''
          }
        });
      });
    });

    const gallery = root.querySelector('[data-product-gallery]');
    if (gallery) {
      gallery.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
        thumb.addEventListener('click', () => switchGalleryMedia(root, thumb.dataset.mediaId));
      });
      bindGallerySwipe(gallery);
      bindGalleryZoom(gallery);
      const activeMedia = gallery.querySelector('[data-gallery-media].is-active');
      const zoomBtn = gallery.querySelector('[data-gallery-zoom-trigger]');
      if (zoomBtn) zoomBtn.hidden = activeMedia?.dataset.mediaType !== 'image';
    }

    bindMobileAtcBar(root);
    queuePickupAvailabilityRender(root, { deferred: deferPickupAvailability });
  });
};

const closeOpenNavigationMenus = (exception) => {
  document.querySelectorAll('[data-nav-details][open]').forEach((details) => {
    // Skip the one just opened AND any ancestor of it — relevant now that a
    // mega item relocated into the "More" flyout is itself nested inside
    // that flyout's own <details>. Closing the ancestor would hide the
    // just-opened nested panel's content (native <details> semantics) even
    // though it's still technically "open", leaving the dimming backdrop on
    // with nothing visible or clickable underneath it.
    if (details !== exception && !details.contains(exception)) {
      details.open = false;
    }
  });

  updateNavigationState();
};

const updateNavigationState = () => {
  const hasOpenNavigation = Boolean(document.querySelector('[data-nav-details][open]'));

  document.querySelectorAll('[data-nav-backdrop]').forEach((backdrop) => {
    backdrop.classList.toggle('is-visible', hasOpenNavigation);
  });

  document.querySelectorAll('.site-header--superstore').forEach((header) => {
    header.classList.toggle('is-nav-open', hasOpenNavigation);
  });
};

const setActiveDepartmentPanel = (menu, panelId, { focusTab = false } = {}) => {
  let activeTab = null;

  menu.querySelectorAll('[data-department-tab]').forEach((tab) => {
    const isActive = tab.dataset.departmentTab === panelId;
    tab.classList.toggle('is-active', isActive);
    tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    tab.tabIndex = isActive ? 0 : -1;

    if (isActive) {
      activeTab = tab;
    }
  });

  menu.querySelectorAll('[data-department-panel]').forEach((panel) => {
    const isActive = panel.id === panelId;
    panel.classList.toggle('is-active', isActive);
    panel.hidden = !isActive;

    if (isActive) {
      const carousel = panel.querySelector('[data-mega-carousel]');
      if (carousel && carousel._megaRender) {
        window.requestAnimationFrame(carousel._megaRender);
      }
    }
  });

  if (focusTab && activeTab) {
    activeTab.focus({ preventScroll: true });
  }
};

const setDepartmentMenuPanelMaxHeight = (menu) => {
  const panel = menu?.querySelector('.superstore-departments__panel');
  if (!panel) return;

  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const panelTop = panel.getBoundingClientRect().top;
  const availableHeight = Math.max(320, Math.floor(viewportHeight - panelTop - 12));

  menu.style.setProperty('--superstore-department-panel-max-height', `${availableHeight}px`);
};

const syncOpenDepartmentMenuPanelHeights = () => {
  document.querySelectorAll('[data-department-menu][open]').forEach((menu) => {
    setDepartmentMenuPanelMaxHeight(menu);
  });
};

const superstoreHeaderHoverMedia = window.matchMedia('(hover: hover) and (min-width: 990px)');
const isSuperstoreHoverCapableDesktop = () => superstoreHeaderHoverMedia.matches;

const MEGA_CAROUSEL_PER_PAGE = 4;

const initMegaCarousel = (carousel) => {
  if (carousel.dataset.megaCarouselBound === 'true') return;
  carousel.dataset.megaCarouselBound = 'true';

  const viewport = carousel.querySelector('.superstore-mega__carousel-viewport');
  const columns = carousel.querySelector('.superstore-mega__columns');
  const colEls = Array.from(columns.children);
  const controls = carousel.querySelector('.superstore-mega__carousel-controls');
  const prevBtn = carousel.querySelector('.superstore-mega__carousel-btn--prev');
  const nextBtn = carousel.querySelector('.superstore-mega__carousel-btn--next');

  if (colEls.length <= MEGA_CAROUSEL_PER_PAGE) {
    return;
  }

  viewport.tabIndex = -1;
  carousel.classList.add('is-carousel');
  controls.hidden = false;
  prevBtn.hidden = true;
  nextBtn.hidden = false;
  let page = 0;
  const totalPages = Math.ceil(colEls.length / MEGA_CAROUSEL_PER_PAGE);

  const render = () => {
    if (!viewport.offsetWidth) return;
    const gap = parseFloat(getComputedStyle(columns).columnGap) || 22;
    const colWidth = (viewport.offsetWidth - (MEGA_CAROUSEL_PER_PAGE - 1) * gap) / MEGA_CAROUSEL_PER_PAGE;
    columns.style.transform = `translateX(-${page * MEGA_CAROUSEL_PER_PAGE * (colWidth + gap)}px)`;
    prevBtn.hidden = page === 0;
    nextBtn.hidden = page >= totalPages - 1;
  };

  prevBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (page > 0) {
      viewport.focus({ preventScroll: true });
      page--;
      render();
    }
  });
  nextBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (page < totalPages - 1) {
      viewport.focus({ preventScroll: true });
      page++;
      render();
    }
  });

  const parentDetails = carousel.closest('[data-nav-details]');
  if (parentDetails) {
    parentDetails.addEventListener('toggle', () => {
      if (parentDetails.open) { page = 0; window.requestAnimationFrame(render); }
    });
  }

  carousel._megaRender = render;

  const ro = new ResizeObserver(render);
  ro.observe(viewport);
};

const initNavOverflow = () => {
  const nav = document.querySelector('.site-nav--superstore');
  if (!nav) return;

  const row = nav.closest('.site-header__category-inner');
  if (!row) return;

  const moreEl = nav.querySelector('[data-nav-more]');
  const moreLinks = nav.querySelector('[data-nav-more-links]');
  if (!moreEl || !moreLinks) return;

  const items = Array.from(nav.children).filter((el) => !el.hasAttribute('data-nav-more'));
  if (!items.length) return;

  const update = () => {
    // Move any item parked in the "More" flyout from a previous pass back to
    // its original spot in the row before re-measuring — relocating the
    // actual node (not cloning), so a mega item's <details>/<summary>/
    // dropdown stays the one real, event-bound element throughout. Force-
    // close one first if it's open, so its toggle handler moves its
    // relocated wide panel back before the item itself moves — otherwise
    // the panel would be left behind, orphaned under .category-row.
    items.forEach((item) => {
      item.style.display = '';
      if (item.parentElement === moreLinks) {
        if (item.open) item.open = false;
        nav.insertBefore(item, moreEl);
      }
    });
    moreEl.hidden = false;

    void nav.getBoundingClientRect();

    const navWidth = nav.getBoundingClientRect().width;
    if (!navWidth) return;

    const gap = parseFloat(getComputedStyle(nav).gap) || 3;
    const moreW = moreEl.getBoundingClientRect().width + gap;

    const widths = items.map((item, i) => item.getBoundingClientRect().width + (i > 0 ? gap : 0));
    const totalWidth = widths.reduce((sum, w) => sum + w, 0);

    if (totalWidth <= navWidth) {
      moreEl.hidden = true;
      return;
    }

    const cutoff = navWidth - moreW;
    let used = 0;
    let lastFit = -1;

    for (let i = 0; i < items.length; i++) {
      if (used + widths[i] <= cutoff) {
        used += widths[i];
        lastFit = i;
      } else {
        break;
      }
    }

    // Relocate (not recreate) each overflowing item into the "More" flyout,
    // in order, so mega items keep their real dropdown panel instead of
    // degrading to a flattened, non-interactive link.
    for (let i = lastFit + 1; i < items.length; i++) {
      moreLinks.appendChild(items[i]);
    }
  };

  update();
  document.fonts.ready.then(update);
  new ResizeObserver(update).observe(nav);
};

// A mega item's wide dropdown panel (.site-nav__dropdown--primary-wide)
// positions itself with `left: var(--header-edge-padding)` relative to
// whichever ancestor is actually positioned — normally
// .site-header__category-row, which spans the full header width starting
// at the page edge, so that lands correctly. A mega item relocated into
// the "More" overflow flyout (see initNavOverflow) is nested inside that
// flyout's own narrow, positioned panel instead, so the same `left` value
// resolves against THAT narrow box and runs off-screen.
//
// Two things were already tried and reverted here: switching the panel's
// CSS to position:fixed broke every mega item everywhere (the inherited
// `top: calc(100%...)` stopped resolving sanely once the containing block
// changed), and physically relocating the panel element out of its
// <details> broke the global outside-click-to-close handler (which looks
// for a `[data-nav-details]` ancestor to decide a click is "inside" a
// menu) and meant fully closing the "More" <details> to tidy up the list
// — which, since a closed <details> hides ALL of its descendants
// regardless of their own styling, hid the panel's content too even
// though it was still technically "open", leaving the dimming backdrop
// stuck on with no visible or clickable control left to undo it.
//
// This version leaves the panel exactly where it already lives in the DOM
// (inside its own <details>, inside the "More" list) — so none of that
// breaks — and instead: (1) keeps the panel's CSS position:absolute
// completely untouched (switching to position:fixed was also tried and,
// while it didn't break the open/close interaction, it visibly broke the
// 3-column heading grid inside — some layout interaction with the fixed
// positioning scheme that wasn't worth chasing further when a simpler, more
// conservative fix was available) and instead overrides only the numeric
// `top`/`left` values, computed from the panel's own real offsetParent (its
// actual CSS containing block, whatever ancestor that happens to be) so the
// math is correct regardless of nesting depth — and (2) "minimizes" the
// list by hiding its other, inactive items rather than closing the list's
// own <details>, so the active item's own label stays visible and
// clickable to close it normally.
const repositionOverflowMegaPanel = (details) => {
  const panel = details.querySelector(':scope > .site-nav__dropdown--primary-wide');
  if (!panel) return;

  const moreList = details.closest('[data-nav-more-links]');

  if (!moreList) {
    if (panel.dataset.overflowPositioned === 'true') {
      delete panel.dataset.overflowPositioned;
      panel.style.removeProperty('top');
      panel.style.removeProperty('left');
    }
    return;
  }

  if (details.open) {
    Array.from(moreList.children).forEach((sibling) => {
      if (sibling !== details) sibling.hidden = true;
    });

    const anchor = document.querySelector('.site-header--superstore .site-header__category-row');
    const containingBlock = panel.offsetParent;
    if (anchor && containingBlock) {
      const anchorRect = anchor.getBoundingClientRect();
      const cbRect = containingBlock.getBoundingClientRect();
      const edgePadding = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-edge-padding')
      ) || 24;
      panel.dataset.overflowPositioned = 'true';
      panel.style.top = `${anchorRect.bottom - cbRect.top + 6}px`;
      panel.style.left = `${anchorRect.left - cbRect.left + edgePadding}px`;
    }
  } else {
    // Only restore every sibling's visibility if nothing else in the list
    // is open. Switching directly from one "More" item to another closes
    // this one via closeOpenNavigationMenus before the newly-opened one's
    // own handler runs (deferred, since setting .open via script queues
    // its toggle event rather than firing it inline) — unconditionally
    // restoring here would undo the visibility the newly-active item just
    // set for itself.
    const stillHasOpenSibling = moreList.querySelector(':scope > [data-nav-details][open]');
    if (!stillHasOpenSibling) {
      Array.from(moreList.children).forEach((sibling) => { sibling.hidden = false; });
    }

    if (panel.dataset.overflowPositioned === 'true') {
      delete panel.dataset.overflowPositioned;
      panel.style.removeProperty('top');
      panel.style.removeProperty('left');
    }
  }
};

const bindSuperstoreHeader = () => {
  document.querySelectorAll('[data-nav-details]').forEach((details) => {
    if (details.dataset.navDetailsBound === 'true') return;
    details.dataset.navDetailsBound = 'true';

    details.addEventListener('toggle', () => {
      if (details.open) {
        closeOpenNavigationMenus(details);
      }

      updateNavigationState();
      repositionOverflowMegaPanel(details);

      if (details.matches('[data-department-menu]')) {
        if (details.open) {
          window.requestAnimationFrame(() => {
            setDepartmentMenuPanelMaxHeight(details);
          });
        } else {
          details.style.removeProperty('--superstore-department-panel-max-height');
        }
      }
    });

  });

  document.querySelectorAll('[data-department-menu]').forEach((menu) => {
    if (menu.dataset.departmentMenuBound === 'true') return;
    menu.dataset.departmentMenuBound = 'true';

    const firstTab = menu.querySelector('[data-department-tab]');
    if (firstTab) {
      setActiveDepartmentPanel(menu, firstTab.dataset.departmentTab);
    }

    menu.querySelectorAll('[data-department-tab]').forEach((tab) => {
      const activate = () => {
        setActiveDepartmentPanel(menu, tab.dataset.departmentTab);
      };

      tab.addEventListener('click', activate);
      tab.addEventListener('focus', activate);
      tab.addEventListener('mouseenter', () => {
        if (!isSuperstoreHoverCapableDesktop()) return;
        activate();
      });
      tab.addEventListener('keydown', (event) => {
        const navigationKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
        if (!navigationKeys.includes(event.key)) return;

        const tabs = Array.from(menu.querySelectorAll('[data-department-tab]'));
        const currentIndex = tabs.indexOf(tab);
        if (currentIndex === -1) return;

        let nextIndex = currentIndex;

        switch (event.key) {
          case 'ArrowUp':
          case 'ArrowLeft':
            nextIndex = currentIndex === 0 ? tabs.length - 1 : currentIndex - 1;
            break;
          case 'ArrowDown':
          case 'ArrowRight':
            nextIndex = currentIndex === tabs.length - 1 ? 0 : currentIndex + 1;
            break;
          case 'Home':
            nextIndex = 0;
            break;
          case 'End':
            nextIndex = tabs.length - 1;
            break;
        }

        if (nextIndex === currentIndex || !tabs[nextIndex]) return;

        event.preventDefault();
        setActiveDepartmentPanel(menu, tabs[nextIndex].dataset.departmentTab, { focusTab: true });
      });
    });
  });

  document.querySelectorAll('[data-mega-carousel]').forEach(initMegaCarousel);

  if (document.documentElement.dataset.megaToggleBound !== 'true') {
    document.documentElement.dataset.megaToggleBound = 'true';
    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-mega-toggle]');
      if (!toggle) return;
      const links = toggle.previousElementSibling;
      if (!links || !links.hasAttribute('data-mega-links')) return;
      const expanded = toggle.dataset.expanded === 'true';
      links.querySelectorAll('.primary-mega__links-extra').forEach((link) => {
        link.hidden = expanded;
      });
      toggle.dataset.expanded = expanded ? 'false' : 'true';
      toggle.textContent = expanded ? 'See All' : 'Show Less';
    });
  }

  if (document.documentElement.dataset.departmentMenuScrollBound !== 'true') {
    document.documentElement.dataset.departmentMenuScrollBound = 'true';
    window.addEventListener('resize', syncOpenDepartmentMenuPanelHeights);
    window.addEventListener('scroll', syncOpenDepartmentMenuPanelHeights, { passive: true });
  }

  if (document.documentElement.dataset.superstoreHoverModeBound !== 'true') {
    document.documentElement.dataset.superstoreHoverModeBound = 'true';

    const handleHoverModeChange = () => {
      closeOpenNavigationMenus();
      syncOpenDepartmentMenuPanelHeights();
    };

    if (typeof superstoreHeaderHoverMedia.addEventListener === 'function') {
      superstoreHeaderHoverMedia.addEventListener('change', handleHoverModeChange);
    } else if (typeof superstoreHeaderHoverMedia.addListener === 'function') {
      superstoreHeaderHoverMedia.addListener(handleHoverModeChange);
    }
  }

  initNavOverflow();
};

const initStickyHeader = () => {
  const stickyHeader = document.querySelector('[data-sticky-header]');
  if (!stickyHeader) return;

  const toggleSticky = () => {
    stickyHeader.classList.toggle('is-scrolled', window.scrollY > 16);
  };

  toggleSticky();
  window.addEventListener('scroll', toggleSticky, { passive: true });
};

const initFixedHeaderLayout = () => {
  const fixedHeader = document.querySelector('[data-fixed-header]');
  const announcementBar = document.querySelector('[data-announcement-bar]');

  if (!fixedHeader) {
    document.body.classList.remove('has-fixed-storefront-header');
    document.documentElement.style.removeProperty('--fixed-announcement-height');
    document.documentElement.style.removeProperty('--fixed-storefront-stack-height');
    return;
  }

  const setOffsets = () => {
    const announcementHeight = announcementBar ? announcementBar.offsetHeight : 0;
    const headerHeight = fixedHeader.offsetHeight;

    document.documentElement.style.setProperty('--fixed-announcement-height', `${announcementHeight}px`);
    document.documentElement.style.setProperty('--fixed-storefront-stack-height', `${announcementHeight + headerHeight}px`);
    document.body.classList.add('has-fixed-storefront-header');
  };

  setOffsets();
  window.addEventListener('resize', setOffsets);

  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(() => setOffsets());
    observer.observe(fixedHeader);

    if (announcementBar) {
      observer.observe(announcementBar);
    }
  }
};

const bindFooterBackToTop = () => {
  const button = document.querySelector('[data-back-to-top-button]');
  if (!button || button.dataset.backToTopBound === 'true') return;
  button.dataset.backToTopBound = 'true';

  button.removeAttribute('hidden');
  window.addEventListener('scroll', () => {
    button.classList.toggle('is-visible', window.scrollY > 400);
  }, { passive: true });
  button.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
};

const bindGlobalInteractions = () => {
  document.addEventListener('click', (event) => {
    if (event.__superstoreDrawerHandled === true) return;

    const dialogTrigger = event.target.closest('[data-dialog-open]');
    if (dialogTrigger) {
      openDialog(document.getElementById(dialogTrigger.getAttribute('data-dialog-open')));
      return;
    }

    const dialogCloseButton = event.target.closest('[data-dialog-close]');
    if (dialogCloseButton) {
      closeDialog(dialogCloseButton.closest('dialog'));
      return;
    }

    const trigger = event.target.closest('[data-drawer-trigger]');
    if (trigger) {
      if (trigger.tagName === 'A') {
        event.preventDefault();
      }

      openDrawer(trigger.getAttribute('data-drawer-trigger'));
      return;
    }

    const closeButton = event.target.closest('[data-drawer-close]');
    if (closeButton) {
      // A close control nested INSIDE its drawer (the usual case) needs no
      // value — closest('[data-drawer]') finds it via ancestry. One that
      // isn't a descendant (e.g. the facets mobile overlay, which has to
      // live as a sibling of #facets-panel so it survives that panel's
      // AJAX innerHTML swaps) passes its target explicitly instead.
      const explicitTarget = closeButton.getAttribute('data-drawer-close');
      closeDrawer(explicitTarget || closeButton.closest('[data-drawer]'));
      return;
    }

    if (!event.target.closest('[data-nav-details]')) {
      closeOpenNavigationMenus();
    }

    const quantityButton = event.target.closest('[data-quantity] button');
    if (!quantityButton) return;

    const wrapper = quantityButton.closest('[data-quantity]');
    const input = wrapper?.querySelector('input');
    if (!input) return;

    const step = Number(quantityButton.dataset.step || 0);
    const nextValue = Math.max(Number(input.min || 1), Number(input.value || 1) + step);
    input.value = nextValue;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    document.querySelectorAll('[data-drawer].is-open').forEach((drawer) => {
      closeDrawer(drawer);
    });

    document.querySelectorAll('dialog[open]').forEach((dialog) => {
      closeDialog(dialog);
    });

    closeOpenNavigationMenus();
  });
};

if (!customElements.get('laki-search')) {
  customElements.define('laki-search', PredictiveSearch);
}

if (!customElements.get('laki-cart')) {
  customElements.define('laki-cart', CartDrawer);
}

const initCriticalThemeFeatures = () => {
  if (hasElements('form[data-ajax-cart-form]')) {
    bindAjaxCartForms();
  }

  if (hasElements('[data-product-root]')) {
    bindProductEnhancements({ deferPickupAvailability: true });
  }

  if (hasElements('[data-facets-form]')) {
    bindFacetForms();
  }

  if (hasElements('[data-pickup-dialog]')) {
    bindPickupDialogs();
  }

  if (hasElements('[data-nav-details], [data-department-menu]')) {
    bindSuperstoreHeader();
  }
};

const matchVariantFromOptions = (variants, selectedValues) => {
  return variants.find((v) =>
    v.options.every((opt, i) => opt === selectedValues[i])
  ) || null;
};

const bindQuickAdd = () => {
  const dialog = document.querySelector('[data-quick-add-dialog]');
  const inner = dialog?.querySelector('[data-quick-add-dialog-inner]');
  if (!dialog || !inner) return;

  const resetDialog = () => {
    inner.innerHTML = '<div class="quick-add-loading" aria-hidden="true"></div>';
  };

  const bindPanelOptions = (panel) => {
    const variantsScript = panel.querySelector('[data-product-variants-json]');
    if (!variantsScript) return;

    let variants;
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (_) {
      return;
    }

    const idInput = panel.querySelector('[data-variant-id-input]');
    const priceEl = panel.querySelector('[data-quick-add-price]');
    const atcBtn = panel.querySelector('[data-add-to-cart]');
    const optionCount = panel.querySelectorAll('[data-option-input]:first-of-type').length;
    const optionInputs = panel.querySelectorAll('[data-option-input]');

    const getSelectedValues = () => {
      const values = [];
      const positions = [...new Set([...optionInputs].map((i) => i.dataset.optionPosition))];
      positions.forEach((pos) => {
        const checked = panel.querySelector(`[data-option-position="${pos}"]:checked`);
        if (checked) values.push(checked.value);
      });
      return values;
    };

    const updateVariant = () => {
      const selected = getSelectedValues();
      const variant = matchVariantFromOptions(variants, selected);

      if (idInput) idInput.value = variant ? variant.id : '';
      if (priceEl) priceEl.textContent = variant ? formatMoney(variant.price) : '';
      if (atcBtn) {
        atcBtn.disabled = !variant || !variant.available;
        atcBtn.textContent = variant
          ? (variant.available ? window.theme.strings.addToCart : window.theme.strings.soldOut)
          : window.theme.strings.unavailable || window.theme.strings.soldOut;
      }
    };

    optionInputs.forEach((input) => {
      input.addEventListener('change', updateVariant);
    });
  };

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-quick-add-trigger]');
    if (!trigger) return;

    const productUrl = trigger.dataset.productUrl;
    if (!productUrl) return;

    resetDialog();
    dialog.showModal();
    document.body.classList.add('overflow-hidden');

    const sectionUrl = `${productUrl}${productUrl.includes('?') ? '&' : '?'}section_id=quick-add`;

    fetch(sectionUrl)
      .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
      .then((markup) => {
        const panel = parseHTML(markup).querySelector('[data-quick-add-panel]');
        if (!panel) throw new Error('panel missing');
        inner.innerHTML = '';
        inner.appendChild(panel);
        bindPanelOptions(panel);
        bindAjaxCartForms(inner);

        inner.querySelector('[data-quick-add-close]')?.addEventListener('click', () => {
          dialog.close();
        });
      })
      .catch(() => {
        dialog.close();
        window.location.href = productUrl;
      });
  });

  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) dialog.close();
  });

  dialog.addEventListener('close', () => {
    document.body.classList.remove('overflow-hidden');
    resetDialog();
  });
};

const initDeferredThemeFeatures = () => {
  const deferredTasks = [];

if (hasElements('[data-quick-add-dialog]')) {
    deferredTasks.push(() => bindQuickAdd());
  }

  if (hasElements('.facets-layout')) {
    deferredTasks.push(() => bindFacets());
  }

  if (hasElements('[data-view-toggle-scope]')) {
    deferredTasks.push(() => bindViewToggles());
  }

  if (hasElements('[data-parts-finder]')) {
    deferredTasks.push(() => bindPartsFinder());
  }

  if (hasElements('[data-related-products]')) {
    deferredTasks.push(() => bindRelatedProducts());
  }

  if (hasElements('.fbt')) {
    deferredTasks.push(() => bindFrequentlyBoughtTogether());
  }

  if (hasElements('[data-fbt-auto]')) {
    deferredTasks.push(() => bindFrequentlyBoughtTogetherAuto());
  }

  if (hasElements('[data-complementary-products]')) {
    deferredTasks.push(() => bindComplementaryProducts());
  }

  if (hasElements('[data-faq-root]')) {
    deferredTasks.push(() => bindFaqSections());
  }

  if (hasElements('[data-stage-department-root], [data-stage-carousel], [data-stage-card-carousel], [data-stage-bundle-add]')) {
    deferredTasks.push(() => bindHomeSuperstage());
  }

  if (hasElements('[data-footer-root]')) {
    deferredTasks.push(() => bindFooterBackToTop());
  }

  scheduleDeferredTasks(deferredTasks, { timeout: 1800 });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    bindGlobalInteractions();
    initFixedHeaderLayout();
    initStickyHeader();
    initCriticalThemeFeatures();
    initDeferredThemeFeatures();
  }, { once: true });
} else {
  bindGlobalInteractions();
  initFixedHeaderLayout();
  initStickyHeader();
  initCriticalThemeFeatures();
  initDeferredThemeFeatures();
}
